import React from 'react';
import PropTypes from 'prop-types';
import {area as d3Area, line as d3Line} from 'd3-shape';
import Interpolation from 'constants/interpolation';
import * as LayoutMode from 'constants/layout-mode';
import {isActive} from 'utils/interaction-util';
import {getColumnCenterX, getFullWidthX} from 'utils/layout-util';
import {isValid} from 'validators/number-validator';
import Colors from 'common/constants/colors';
import styles from './index.css';

export default class LineSeries extends React.Component {
  static propTypes = {
    activeIndex: PropTypes.number,
    data: PropTypes.array,
    interactive: PropTypes.bool,
    interpolate: PropTypes.oneOf(Object.values(Interpolation)),
    layoutMode: PropTypes.oneOf(Object.values(LayoutMode)),
    scaleY: PropTypes.func,
    style: PropTypes.object,
    tickWidth: PropTypes.number,
    transform: PropTypes.string,
    width: PropTypes.number,
  };

  static defaultProps = {
    activeIndex: -1,
    data: [],
    interactive: true,
    interpolate: Interpolation.MONOTONE,
    layoutMode: LayoutMode.COLUMN_LAYOUT,
    scaleY: () => {},
    style: {},
    tickWidth: 0,
    transform: '',
    width: 0,
  };

  static getRadius() {
    return 4;
  }

  getLayoutFuncX(layoutMode, tickWidth, width, dataLength) {
    return layoutMode === LayoutMode.COLUMN_LAYOUT
      ? (datum, index) => getColumnCenterX(tickWidth, index)
      : (datum, index) => getFullWidthX(width, dataLength, index);
  }

  getMarkerX(layoutMode, tickWidth, width, dataLength, index) {
    return layoutMode === LayoutMode.COLUMN_LAYOUT
      ? getColumnCenterX(tickWidth, index)
      : getFullWidthX(width, dataLength, index);
  }

  getMarkerY(scale, value) {
    if (scale && isValid(value)) {
      return Math.round(scale(value));
    }
    return 0;
  }

  render() {
    const {
      activeIndex,
      data,
      interactive,
      interpolate,
      layoutMode,
      scaleY,
      style,
      tickWidth,
      transform,
      width,
    } = this.props;

    const layoutFuncX = this.getLayoutFuncX(
      layoutMode,
      tickWidth,
      width,
      data.length,
    );

    const area = d3Area()
      .defined((datum) => isValid(datum.value))
      .curve(interpolate)
      .x(layoutFuncX)
      .y0(scaleY(0))
      .y1((datum) => Math.round(scaleY(datum.value)));

    const line = d3Line()
      .defined((datum) => isValid(datum.value))
      .curve(interpolate)
      .x(layoutFuncX)
      .y((datum) => Math.round(scaleY(datum.value)));

    return (
      <g className={styles.LineSeries} transform={transform}>
        {style.fill ? (
          <path
            d={area(data)}
            style={{
              fill: style.fill,
            }}
          />
        ) : null}

        <path
          d={line(data)}
          style={{
            stroke: style.stroke,
          }}
        />

        {data.map((datum, index) => {
          const active = isActive(activeIndex, index, interactive);
          if (active) {
            const cx = this.getMarkerX(
              layoutMode,
              tickWidth,
              width,
              data.length,
              index,
            );
            const cy = this.getMarkerY(scaleY, datum.value);
            return datum.value !== null ? (
              <circle
                cx={cx}
                cy={cy}
                key={`marker-${index}`}
                r={LineSeries.getRadius()}
                style={{
                  fill: Colors.white,
                  stroke: style.stroke,
                }}
              />
            ) : null;
          }
          return null;
        })}
      </g>
    );
  }
}
